// LoginModal.js
import React, { useState, useRef } from 'react';
import { Modal, View, Text, TextInput, TouchableOpacity, StyleSheet, Image, Alert, Platform } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { useUser } from '../../../../context/UserContext';
import { useCart } from '../../../../context/CartContext';
import { BACKEND_URL } from '../../../../Config/Const';
import CreateAccount from './CreateAccount';
import VerificationCodeModal from './VerificationCodeModal';

import RecuperarContrasena from './RecuperarContrasena';
import { colores } from '../../../../styles/variablesCss';
import { fuentes } from '../../../../styles/variablesCss';
import { trackUserLogin } from '../../../../utils/analytics';

const Login = ({ visible, onClose, masCompras }) => {
  const [mail, setMail] = useState('');
  const [cont, setCont] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showCreateAccount, setShowCreateAccount] = useState(false);
  const [showRecuperar, setShowRecuperar] = useState(false);
  const [showVerification, setShowVerification] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [recuperarLoading, setRecuperarLoading] = useState(false);
  const [recuperarError, setRecuperarError] = useState('');
  const { updateUser } = useUser();

  const { cargarProductosCarrito } = useCart();
  const passwordRef = useRef(null);

  const handleLogin = async () => {
    if (!mail || !cont) {
      setError('Por favor complete todos los campos');
      return;
    }
    setIsLoading(true);
    try {
      const loginUrl = `${BACKEND_URL}/rest/usuarios/login?mail=${encodeURIComponent(mail)}&cont=${encodeURIComponent(cont)}`;

      const response = await fetch(loginUrl, {
        method: 'POST',
      });

      const data = await response.json();
      if (response.ok && data.dsResultado?.Usuario?.[0]) {

        const user = data.dsResultado.Usuario[0];
        
        // Actualizar el contexto con los datos necesarios
        const userData = {
          codUsu: user.codUsu?.toString(),
          nombre: user.nombre,
          mail: user.mail,
          genero: user.genero,
          fechaNac: user.fechaNac,
          cel: user.cel,
          tel: user.tel
        };
        
        await updateUser(userData);
        
        // Registrar login en Google Analytics
       //trackUserLogin(user.codUsu);
        // Refrescar carrito con los datos del usuario logueado
        try {
          await cargarProductosCarrito();
        } catch (e) {
          // Silencioso: si falla, no bloquea el login
          
        }
        
        // Si estamos en web y en la sección masCompras, recargar la página
        if (Platform.OS === 'web' && typeof window !== 'undefined') {
          const params = new URLSearchParams(window.location.search);
          if (params.get('masCompras') === 'true') {
            // Esperar un poco para que se actualice el contexto
            setTimeout(() => {
              window.location.reload();
            }, 500);
            return;
          }
        }
        
        onClose();
      } else {
        const resultRow = data?.dsResultado?.resultado?.[0];
        const mensaje = (resultRow?.mensaje || '').toString().trim().toLowerCase();
        if (resultRow && resultRow.resultado === false && mensaje === 'validar') {
          setError('');
          setShowVerification(true);
          return;
        }
        setError('Credenciales inválidas');
      }
    } catch (error) {
      console.error('Error en login:', error);
      setError('Error de conexión');
    } finally {

      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setShowCreateAccount(false);
    setShowRecuperar(false);
    setShowVerification(false);
    setMail('');
    setCont('');
    setError('');
    setRecuperarLoading(false);
    setRecuperarError('');

    onClose();
  };

  const handleRecuperarSubmit = async (email) => {
    const mailValue = (email || '').trim();
    if (!mailValue) {
      setRecuperarError('Por favor ingresá tu correo electrónico');
      return;
    }

    setRecuperarError('');
    setRecuperarLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/rest/usuarios/recuperar-clave`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          request: {
            mail: mailValue,
          },
        }),
      });

      let data;
      try {
        data = await response.json();
      } catch (e) {
        data = null;
      }

      if (response.ok) {
        setShowRecuperar(false);
        Alert.alert('Recuperación de contraseña', 'Si el correo existe, te enviaremos las instrucciones para recuperar tu contraseña.');
      } else {
        const message = data?.dsResultado?.resultado?.[0]?.mensaje || 'No se pudo procesar la solicitud';
        setRecuperarError(message);
      }
    } catch (e) {
      setRecuperarError('Error de conexión');
    } finally {
      setRecuperarLoading(false);
    }
  };

  if (showCreateAccount) {
    return <CreateAccount visible={true} onClose={() => setShowCreateAccount(false)} />;
  }

  return (
    <Modal
      visible={visible}
      transparent={true}
      animationType="slide"
      onRequestClose={handleClose}
    >
      <View style={styles.modalOverlay}>
        <View style={styles.modalContent}>
          <TouchableOpacity
            style={styles.closeButton}
            onPress={handleClose}
            hitSlop={{ top: 10, right: 10, bottom: 10, left: 10 }}
            accessibilityRole="button"
            accessibilityLabel="Cerrar"
          >
            <Text style={styles.closeButtonText}>×</Text>
          </TouchableOpacity>

          <View style={styles.contentRow}>
            {/* Columna izquierda: textos */}
            <View style={styles.leftColumn}>
              <Text style={styles.heroTitle}>Ingresá a tu cuenta</Text>
              <Text style={styles.heroSubtitle}>y comprá desde donde quieras.</Text>
            </View>

            {/* Columna derecha: formulario */}
            <View style={styles.rightColumn}>
              <TextInput
                style={styles.inputPill}
                placeholder="Correo electrónico"
                value={mail}
                onChangeText={setMail}
                keyboardType="email-address"
                autoCapitalize="none"
                placeholderTextColor="#A0A0A0"
                returnKeyType="next"
                onSubmitEditing={() => passwordRef.current && passwordRef.current.focus()}
                onKeyPress={(e) => {
                  if (e?.nativeEvent?.key === 'Enter') {
                    // En algunas plataformas web, onSubmitEditing no dispara en el primer input
                    if (passwordRef.current) passwordRef.current.focus();
                  }
                }}
              />

              <View style={styles.passwordPillContainer}>
                <TextInput
                  style={styles.passwordInput}
                  placeholder="Contraseña"
                  value={cont}
                  onChangeText={setCont}
                  secureTextEntry={!showPassword}
                  placeholderTextColor="#A0A0A0"
                  ref={passwordRef}
                  returnKeyType="go"
                  onSubmitEditing={handleLogin}
                  onKeyPress={(e) => {
                    if (e?.nativeEvent?.key === 'Enter') {
                      handleLogin();
                    }
                  }}
                />
                <TouchableOpacity
                  style={styles.eyeIcon}
                  onPress={() => setShowPassword(!showPassword)}
                  hitSlop={{ top: 6, right: 6, bottom: 6, left: 6 }}
                >
                  <MaterialIcons
                    name={showPassword ? "visibility" : "visibility-off"}
                    size={22}
                    color="#666"
                  />
                </TouchableOpacity>
              </View>

              {error ? <Text style={styles.errorText}>{error}</Text> : null}

              <View style={styles.actionsRow}>
                <TouchableOpacity onPress={() => setShowRecuperar(true)}>
                  <Text style={styles.forgotText}>¿Olvidaste tu contraseña?</Text>
                </TouchableOpacity>

                <TouchableOpacity 
                  style={styles.primaryButton}
                  onPress={handleLogin}
                  disabled={isLoading}
                >
                  <Text style={styles.primaryButtonText}>Ingresar</Text>
                </TouchableOpacity>
              </View>

              <TouchableOpacity 
                style={styles.createAccountButton}
                onPress={() => setShowCreateAccount(true)}
              >
                <Text style={styles.createAccountText}>¿No tenés cuenta? Creala acá</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </View>
      {/* Modal anidado para recuperar contraseña */}
      {showRecuperar && (
        <Modal
          visible={showRecuperar}
          transparent={true}
          animationType="slide"
          onRequestClose={() => {
            setRecuperarError('');
            setShowRecuperar(false);
          }}
        >
          <View style={styles.modalOverlay}>
            <View style={styles.nestedModalContent}>
              <TouchableOpacity
                style={[styles.closeButton, { right: -5, top: -18 }]}
                onPress={() => {
                  setRecuperarError('');
                  setShowRecuperar(false);
                }}
                hitSlop={{ top: 10, right: 10, bottom: 10, left: 10 }}
                accessibilityRole="button"
                accessibilityLabel="Cerrar"
              >
                <Text style={styles.closeButtonText}>×</Text>
              </TouchableOpacity>
              <Text style={styles.title}>Recuperar contraseña</Text>

              <RecuperarContrasena
                onSubmit={handleRecuperarSubmit}
                isLoading={recuperarLoading}
                error={recuperarError}
              />
            </View>
          </View>
        </Modal>
      )}
      <VerificationCodeModal
        visible={showVerification}
        mail={mail}
        onClose={(verified) => {
          setShowVerification(false);
          if (verified) {
            handleClose();
          }
        }}
      />
    </Modal>
  );
};

const styles = StyleSheet.create({
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: colores.blanco,
    borderRadius: 10,
    width: '94%',
    maxWidth: 900,
    padding: 24,
    zIndex: 100000000,
  },
  nestedModalContent: {
    backgroundColor: colores.blanco,
    borderRadius: 10,
    width: '92%',
    maxWidth: 560,
    padding: 16,
    zIndex: 100000000,
  },
  contentRow: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 20,
  },
  leftColumn: {
    flex: 1,
    paddingRight: 10,
  },
  rightColumn: {
    flex: 1,
    paddingLeft: 10,
    alignItems: 'stretch',
  },
  closeButton: {
    position: 'absolute',
    right: -5,
    top: -18,
    padding: 10,
    zIndex: 100000000,
    cursor: 'pointer',
  },
  closeButtonText: {
    fontSize: 30,
    color: colores.rojo || '#E31837',
    fontWeight: 'bold',
  },
   title: {
      fontSize: 18,
      fontFamily: fuentes.bold,
      color: colores.rojo,
      borderBottomWidth: 1,
      borderBottomColor: '#f0f0f0',
      paddingBottom: 5,
    },
  heroTitle: {
    color: '#E31837',
    fontWeight: '800',
    fontSize: 28,
    marginBottom: 4,
  },
  heroSubtitle: {
    color: '#333',
    fontSize: 18,
    marginBottom: 18,
  },
  heroNote: {
    color: '#8A8A8A',
    fontSize: 12,
  },
  inputPill: {
    height: 42,
    width: '100%',
    backgroundColor: '#FFFFFF',
    borderRadius: 21,
    paddingHorizontal: 16,
    borderWidth: 1,
    borderColor: '#E5E5E5',
    marginBottom: 10,
  },
  passwordPillContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 42,
    width: '100%',
    backgroundColor: '#FFFFFF',
    borderRadius: 21,
    paddingHorizontal: 16,
    borderWidth: 1,
    borderColor: '#E5E5E5',
    marginBottom: 10,
  },
  passwordInput: {
    flex: 1,
    height: 42,
  },
  eyeIcon: {
    padding: 4,
  },
  actionsRow: {
    marginTop: 6,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
  },
  forgotText: {
    color: '#9E9E9E',
    fontSize: 12,
  },
  primaryButton: {
    backgroundColor: '#E31837',
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 6,
  },
  primaryButtonText: {
    color: '#FFFFFF',
    fontWeight: '700',
    fontSize: 14,
  },
  createAccountButton: {
    marginTop: 20,
    padding: 10,
  },
  createAccountText: {
    color: '#666',
    fontSize: 14,
    textDecorationLine: 'underline',
  },
  errorText: {
    color: '#E31837',
    marginBottom: 10,
    textAlign: 'center',
  },
  // Responsive: en pantallas chicas apilar columnas
  ...(typeof window !== 'undefined' && window.innerWidth <= 640 ? {
    contentRow: {
      width: '100%',
      flexDirection: 'column',
      alignItems: 'stretch',
      gap: 12,
    },
    leftColumn: {
      paddingRight: 0,
    },
    rightColumn: {
      paddingLeft: 0,
    },
  } : {}),
});

export default Login;